import React, { useEffect, useState } from 'react';
import PropTypes from "prop-types";
import { DELIVERY_PARTNERS, DELIVERY_PARTNER_IDS_MAP } from './constants';
import { getDeliveryPartners } from './helper';
import DTDCForm from './DTDCForm';
import ShiprocketForm from './ShiprocketForm';
import ManualDeliveryForm from './ManualDeliveryForm';
import OpenBoxPhotoField from './OpenBoxPhotoField';
import ExtraItemsNotice from './ExtraItemsNotice';

const AUTOMATED_PARTNER_OPTIONS = [
    DELIVERY_PARTNERS.DTDC,
    DELIVERY_PARTNERS.SHIPROCKET,
];

const DeliveryCreateModal = ({ orderId, mapUserPatient, address, onClose, orderData }) => {
    // Open-box proof photos. Held in the SHELL, not in the partner forms, so DTDC,
    // Shiprocket and manual all get the field once and behave identically.
    // `boxImages` are Azure blob names (persisted); `boxPreviews` are the matching SAS URLs,
    // used only to render thumbnails — they expire and are never sent to the API.
    const [boxImages, setBoxImages] = useState([]);
    const [boxPreviews, setBoxPreviews] = useState([]);
    const [isManual, setIsManual] = useState(false);
    const [selectedDeliveryPartner, setSelectedDeliveryPartner] = useState(DELIVERY_PARTNERS.DTDC.partnerId);
    const [manualDeliveryPartners, setManualDeliveryPartners] = useState([]);

    useEffect(() => {
        if (!isManual) {
            return;
        }

        (async () => {
            const partners = await getDeliveryPartners();
            if (partners.length > 0) {
                setManualDeliveryPartners(partners);
                setSelectedDeliveryPartner((current) => {
                    const stillValid = partners.some((partner) => partner.id === current);
                    return stillValid ? current : partners[0].id;
                });
            }
        })();
    }, [isManual]);

    const handleBoxImagesChange = (nextImages, nextPreviews) => {
        setBoxImages(nextImages);
        setBoxPreviews(nextPreviews);
    };

    const handleManualToggle = (checked) => {
        setIsManual(checked);
        if (checked) {
            setSelectedDeliveryPartner(manualDeliveryPartners[0]?.id ?? DELIVERY_PARTNER_IDS_MAP.DTDC);
        } else {
            setSelectedDeliveryPartner(DELIVERY_PARTNERS.DTDC.partnerId);
        }
    };

    const partnerOptions = isManual
        ? manualDeliveryPartners
        : AUTOMATED_PARTNER_OPTIONS.map((partner) => ({ id: partner.partnerId, name: partner.partnerName }));

    const formatPartnerLabel = (partner) => {
        if (typeof partner.name === 'string') {
            return partner.name;
        }
        return partner.partnerName;
    };

    return (
        <div className='flex flex-col gap-[0.9rem]' >
            <div className='flex justify-between' >
                <span className='text-[1.5rem] font-bold' >Create Delivery</span>
                <span className='text-[1.2rem]' >Order ID: <span className='text-[1.2rem] font-semibold' >{`#${orderId}`}</span></span>
            </div>
            <hr />

            {/* "Meds to Pack Separately", written by a pharmacist on the order edit page.
                Shown HERE because this is the moment the box is being made up — it was
                previously only visible on the logistics Orders tab, which the person packing
                has no reason to open. Renders nothing when the order has no note. */}
            <ExtraItemsNotice extraItems={orderData?.additionalDetails?.extraItems} />

            <div className='flex gap-[0.5rem] items-center' >
                <input
                    id='manual-delivery-input'
                    type='checkbox'
                    className='m-0 p-0'
                    checked={isManual}
                    onChange={(e) => handleManualToggle(e.target.checked)}
                />
                <label className='m-0 p-0' htmlFor='manual-delivery-input'>Manual delivery</label>
            </div>
            <div className='flex flex-col gap-[0.9rem]' >
                <div className='flex justify-between' >
                    <span>Select Delivery Partner</span>
                    <select
                        className='w-full'
                        value={selectedDeliveryPartner}
                        onChange={(e) => setSelectedDeliveryPartner(parseInt(e.target.value))}
                        disabled={isManual && manualDeliveryPartners.length === 0}
                    >
                        {
                            partnerOptions.map((partner) => (
                                <option key={partner.id} value={partner.id}>
                                    {formatPartnerLabel(partner)}
                                </option>
                            ))
                        }
                    </select>
                </div>
                {isManual && manualDeliveryPartners.length === 0 && (
                    <span className='text-sm text-gray-500'>Loading delivery partners...</span>
                )}
                <hr />
            </div>
            <OpenBoxPhotoField
                images={boxImages}
                previews={boxPreviews}
                onChange={handleBoxImagesChange}
            />
            <hr />
            {/* The photo is OPTIONAL (product decision, 2026-09-23). It was previously
                required, enforced by a disabled <fieldset> around the partner forms; that
                gate is removed. A delivery can now be created with no open-box photo, and
                the Refunds-style "View Images" action simply does not appear on that row. */}
            <div className='flex flex-col gap-[0.9rem]' >
                {isManual && (
                    <ManualDeliveryForm
                        orderId={orderId}
                        mapUserPatient={mapUserPatient}
                        address={address}
                        deliveryPartnerId={selectedDeliveryPartner}
                        onClose={onClose}
                        orderData={orderData}
                        deliveryBoxImages={boxImages}
                    />
                )}
                {!isManual && selectedDeliveryPartner === DELIVERY_PARTNERS.DTDC.partnerId && (
                    <DTDCForm orderId={orderId} mapUserPatient={mapUserPatient} address={address} onClose={onClose} orderData={orderData} deliveryBoxImages={boxImages} />
                )}
                {!isManual && selectedDeliveryPartner === DELIVERY_PARTNERS.SHIPROCKET.partnerId && (
                    <ShiprocketForm orderId={orderId} mapUserPatient={mapUserPatient} address={address} onClose={onClose} deliveryBoxImages={boxImages} />
                )}
            </div>
        </div >
    )
}

DeliveryCreateModal.propTypes = {
    orderId: PropTypes.number.isRequired,
    mapUserPatient: PropTypes.object.isRequired,
    address: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    orderData: PropTypes.object,
};

export default DeliveryCreateModal
